import { IthHelmet } from "@/components"
import { Breadcrumb, Button, Card, Select, Spin, Space, message } from "antd"
import React, { Fragment, useEffect, useMemo, useState } from "react"
import { Link, useParams, useLocation, useSearchParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import {
    getWorktimeDailyNewApi,
    rejectTimeworksApi,
} from "@/services/apis/worktimeDaily"
import { auditTimeworks, auditTimeworksSz } from "@/services/apis/worktimeDaily"
import { useRequest } from "ahooks"
import AuditTable from "../components/auditTable"
import type { RenderProjectTableData, RenderMajorTableData } from "../worktime"
import { addMultiple } from "@/utils/amount"
import AuditFilter from "../components/auditFilter"
import { k_pageType, getPageTypeLabel } from "./hook"
import { getDayDisplay, addDays, formatDate } from "../components/util"

interface AuditInfoContentProps {
    defaultDate: string
    pageType: string
    onUpdateList: () => void
}

const Index = (props: AuditInfoContentProps) => {
    const { defaultDate, pageType, onUpdateList } = props

    const [isAllWeekday, setIsAllWeekday] = useState(true)
    const [currentDate, setCurrentDate] = useState("")

    const navigate = useNavigate()

    // const { currentDate } = useParams()

    const {
        data: dailyInfo,
        loading: dailyInfoLoading,
        run: runGetWorktimeDaily,
        refresh: refreshWorktimeDaily,
    } = useRequest(getWorktimeDailyNewApi, {
        manual: true,
    })
    useEffect(() => {
        if (!defaultDate) return
        setIsAllWeekday(true)
        setCurrentDate("")
        runGetWorktimeDaily(defaultDate, { type: pageType, isAll: true })
    }, [defaultDate, pageType])


    const {
        run: runRejectTimeworks,
        loading: rejectTimeworksLoading,
    } = useRequest(rejectTimeworksApi, {
        manual: true,
        onSuccess: () => {
            message.success("打回成功")
            refreshWorktimeDaily()
            onUpdateList()
        },
        onError: (error) => {
            message.error(error.message || "打回失败")
        }
    })
    const onRejectTimeworks = (data: Request.RejectTimeworks) => {
        runRejectTimeworks(data)
    }

    // 构建项目级别的表格数据
    const projectTableData: RenderProjectTableData[] = useMemo(() => {
        if (!dailyInfo || !dailyInfo.projects) return []

        return dailyInfo.projects.map((project) => {
            let projectTotalHours = 0
            let marjorMap = new Map<string, RenderMajorTableData>()
            project.users?.forEach((user) => {
                let userWithTotalHours = {
                    ...user,
                    totalHours: addMultiple(1, user.designHours, user.communicationHours, user.travelHours),
                }
                if (marjorMap.has(user.majorName)) {
                    const major = marjorMap.get(user.majorName)!
                    major.totalHours = addMultiple(1, major.totalHours, userWithTotalHours.totalHours || 0)
                    major.users.push(userWithTotalHours)

                }else{
                    marjorMap.set(user.majorName, {
                        name: user.majorName,
                        totalHours: userWithTotalHours.totalHours || 0,
                        users: [userWithTotalHours],
                    })
                }
                projectTotalHours = addMultiple(1, projectTotalHours, userWithTotalHours.totalHours || 0)
            })
            // 按照"建筑、结构、暖通、电气、给排水"这么排列，不在列表中的放在最后
            const sortedMajors = [...marjorMap.values()].sort((a, b) => {
                const order = ["建筑", "结构", "暖通", "电气", "给排水"]
                const indexA = order.indexOf(a.name)
                const indexB = order.indexOf(b.name)
                
                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB
                }
                if (indexA !== -1) return -1
                if (indexB !== -1) return 1
                return 0
            })
            return {
                id: project.id,
                name: project.name,
                code: project.code,
                totalHours: projectTotalHours,
                majors: sortedMajors
            }
        })
    }, [dailyInfo])

    const period = useMemo(() => {
        return isAllWeekday ? "整周汇总" : (currentDate ? getDayDisplay(currentDate) : "")
    }, [isAllWeekday, currentDate])

    // 预估工时弹窗查询的时间区间：整周时取周一~周日，单天时取当天
    const dateRange = useMemo(() => {
        if (isAllWeekday) {
            if (!defaultDate) return { startDate: "", endDate: "" }
            const start = new Date(defaultDate)
            const end = addDays(start, 6)
            return { startDate: defaultDate, endDate: formatDate(end) }
        }
        return { startDate: currentDate, endDate: currentDate }
    }, [defaultDate, isAllWeekday, currentDate])


    const { run: runAuditTimeworks, loading: auditLoading } = useRequest(
    auditTimeworks,
    {
        manual: true,
        onSuccess: () => {
            refreshWorktimeDaily()
            onUpdateList()
        },
        onError: (error) => {
            message.error(error.message || "审批失败")
        }
    }
    )
    // auditTimeworksSz
    const { run: runAuditTimeworksSz, loading: auditLoadingSz } = useRequest(
    auditTimeworksSz,
    {
        manual: true,
        onSuccess: () => {
            refreshWorktimeDaily()
            onUpdateList()
        },
        onError: (error) => {
            message.error(error.message || "审批失败")
        }
    }
    )
    // 审批按钮事件
    const handleAudit = (type: string) => {
        let date = currentDate || defaultDate
        if (!date) return
        if(pageType === 'zc'){
            runAuditTimeworks(date, "", isAllWeekday)
        }else if(pageType === 'sz'){
            runAuditTimeworksSz(date, "", isAllWeekday)
        }
    }

    const onChangeDate = (date: string, isAll: boolean) => {
        let newDate = date
        if(isAll){
            newDate = defaultDate
        } else {
            setCurrentDate(date)
        }
        setIsAllWeekday(isAll)
        runGetWorktimeDaily(newDate, { type: pageType, isAll: isAll })
    }

    return (
        <>
            <div className="absolute left-0 right-0 top-0 bottom-0 flex flex-col p-2 overflow-auto">
                <IthHelmet pageTitle="工时填报明细" report />
                <div className="flex items-center justify-end pb-2">
                    <span className="text-gray-600 text-xs">审批类型： <span className="font-medium">{pageType === k_pageType.sz ? '设总' :  '专负'}</span></span>
                </div>
                

                <div>
                    <Spin spinning={dailyInfoLoading || auditLoading || auditLoadingSz || rejectTimeworksLoading}>
                        <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-12">
                            <main className="max-w-7xl mx-auto px-4 space-y-6 pt-4">
                                <AuditFilter dates={dailyInfo?.dates || []}
                                    isAll={isAllWeekday} currentDate={currentDate || ""}
                                    pageType={pageType || k_pageType.zc}
                                    onChangeDate={onChangeDate}
                                    onAudit={handleAudit}
                                />
                                <AuditTable data={projectTableData} period={period}
                                    isAll={isAllWeekday}
                                    isDefaultExpand={pageType === k_pageType.zc}
                                    currentDate={currentDate || ""}
                                    type={pageType || k_pageType.zc}
                                    startDate={dateRange.startDate}
                                    endDate={dateRange.endDate}
                                    onRejectTimeworks={onRejectTimeworks}
                                    loading={dailyInfoLoading} />
                            </main>
                        </div>
                    </Spin>
                </div>
            </div>
        </>
    )
}

export default Index
